import type { Theme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes } from "react-router";
import DataCollectionPage from "./components/data-collection/DataCollectionPage";
import DataSubmissionPage from "./components/data-collection/submissions/DataSubmissionPage";
import FundraisingListPage from "./components/fundraising/FundraisingListPage";
import FundraisingPage from "./components/fundraising/FundraisingPage";
import AttachmentPreviewRedirect from "./components/inbox/AttachmentPreviewRedirect";
import InboxContainer from "./components/inbox/InboxContainer";
import InboxComponent from "./components/inbox/desktop/InboxComponent";
import Messages from "./components/inbox/desktop/Messages";
import MessageComponent from "./components/inbox/message/MessageComponent";
import MessagesListComponent from "./components/inbox/messagesList/mobile/MessagesListComponent";
import NewsDetailsPage from "./components/news/NewsDetailsPage";
import NewsPage from "./components/news/NewsPage";
import NewsMobilePage from "./components/news/mobile/NewsMobilePage";
import PortfolioMobilePage from "./components/portfolio/PortfolioMobilePage";
import PortfolioPage from "./components/portfolio/PortfolioPage";
import InvestorSettings from "./components/settings/InvestorSettings";
import SettingsPage from "./components/settings/SettingsPage";
import UserSettingsPage from "./components/settings/UserSettingsPage";
import AccountSettings from "./components/settings/lp/account/AccountSettings";
import BankSettings from "./components/settings/lp/bank/BankSettings";
import ChangeFundAssignmentsPage from "./components/settings/lp/bank/changeFundAssignments/ChangeFundAssignmentsPage";
import NewBankAccountPage from "./components/settings/lp/bank/newBankAccount/NewBankAccountPage";
import ContactSettings from "./components/settings/lp/contacts/ContactSettings";
import NewContactPage from "./components/settings/lp/contacts/newContact/NewContactPage";
import MyProfile from "./components/settings/profile/myProfile/MyProfile";
import PasswordAuth from "./components/settings/profile/passwordAuth/PasswordAuth";
import DashboardWidgetsPage from "./components/widgets/DashboardWidgetsPage";
import DashboardWidgetsMobilePage from "./components/widgets/mobile/DashboardWidgetsMobilePage";
import useClientContext from "./contexts/useClientContext";
import { pageRoutes } from "./routes";
import { investorsWithSettingsAccessSelector } from "./store/state/user/selectors";

const Pages = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between("sm", "lg"));

  const { clientCode, isFeatureEnabled, portfolioWidget, isNewsPageAvailable, isDashboardPageAvailable } =
    useClientContext();

  const hasInvestorSettingsAccess = useSelector(investorsWithSettingsAccessSelector).length > 0;

  const getHomeRoute = () => {
    if (isDashboardPageAvailable) {
      return `/${clientCode}/${pageRoutes.dashboard}`;
    }
    if (isNewsPageAvailable) {
      return `/${clientCode}/${pageRoutes.news}`;
    }
    if (isFeatureEnabled("Inbox")) {
      return `/${clientCode}/${pageRoutes.inbox}`;
    }
    if (isFeatureEnabled("Fundraising")) {
      return `/${clientCode}/${pageRoutes.fundraising}`;
    }
    if (isFeatureEnabled("DataCollection")) {
      return `/${clientCode}/${pageRoutes.dataCollection}`;
    }
    return `/${clientCode}/${pageRoutes.user}/profile`;
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate replace to={getHomeRoute()} />} />

      <Route path={pageRoutes.settings} element={<SettingsPage />}>
        <Route path="lp" element={<InvestorSettings />}>
          {hasInvestorSettingsAccess && isFeatureEnabled("AccountSettingsViewer") && (
            <Route path="account" element={<AccountSettings />} />
          )}
          {hasInvestorSettingsAccess && isFeatureEnabled("BankAccountsDetailsViewer") && (
            <Route path="bank" element={<BankSettings />} />
          )}
          {hasInvestorSettingsAccess && isFeatureEnabled("Membership") && (
            <Route path="members" element={<ContactSettings />} />
          )}
        </Route>
        {hasInvestorSettingsAccess && isFeatureEnabled("BankAccountsDetailsEditor") && (
          <Route path="lp/bank/new" element={<NewBankAccountPage />} />
        )}
        {hasInvestorSettingsAccess && isFeatureEnabled("BankAccountsDetailsEditor") && (
          <Route path="lp/bank/assign" element={<ChangeFundAssignmentsPage />} />
        )}
        {hasInvestorSettingsAccess && isFeatureEnabled("MembershipEditor") && (
          <Route path="lp/members/new" element={<NewContactPage />} />
        )}
      </Route>

      <Route path={pageRoutes.user} element={<UserSettingsPage />}>
        <Route path="profile" element={<MyProfile />} />
        <Route path="password-auth" element={<PasswordAuth />} />
      </Route>

      {isDashboardPageAvailable && isMobile && (
        <Route path={pageRoutes.dashboard} element={<DashboardWidgetsMobilePage />} />
      )}

      {isDashboardPageAvailable && !isMobile && (
        <Route path={pageRoutes.dashboard} element={<DashboardWidgetsPage />} />
      )}

      {isNewsPageAvailable && isMobile && (
        <Route path={pageRoutes.news} element={<Outlet />}>
          <Route index element={<NewsMobilePage />} />
          <Route path=":id" element={<NewsDetailsPage />} />
        </Route>
      )}

      {isNewsPageAvailable && !isMobile && (
        <Route path={pageRoutes.news} element={<Outlet />}>
          <Route index element={<NewsPage />} />
          <Route path=":id" element={<NewsDetailsPage />} />
        </Route>
      )}

      {isFeatureEnabled("PortfolioCompanies") && portfolioWidget !== undefined && isMobile && (
        <Route path={pageRoutes.portfolio} element={<PortfolioMobilePage />} />
      )}

      {isFeatureEnabled("PortfolioCompanies") && portfolioWidget !== undefined && !isMobile && (
        <Route path={pageRoutes.portfolio} element={<PortfolioPage />} />
      )}

      {isFeatureEnabled("Fundraising") && (
        <Route path={pageRoutes.fundraising} element={<Outlet />}>
          <Route index element={<FundraisingListPage />} />
          <Route path=":id" element={<FundraisingPage />} />
        </Route>
      )}

      {isFeatureEnabled("DataCollection") && (
        <Route path={pageRoutes.dataCollection} element={<Outlet />}>
          <Route index element={<DataCollectionPage />} />
          <Route path="submissions" element={<Outlet />}>
            <Route path=":id" element={<DataSubmissionPage />} />
          </Route>
        </Route>
      )}

      {isFeatureEnabled("Inbox") && isMobile && (
        <Route path={pageRoutes.inbox} element={<InboxContainer />}>
          <Route path=":tag" element={<MessagesListComponent />} />
          <Route path=":tag/:page" element={<MessagesListComponent />} />
          <Route path=":tag/message/:id" element={<MessageComponent />} />
        </Route>
      )}
      {isFeatureEnabled("Inbox") && !isMobile && (
        <Route path={pageRoutes.inbox} element={<InboxContainer keepPage={!isTablet} />}>
          <Route path=":tag" element={<InboxComponent />}>
            <Route index element={<Messages />} />
            <Route path=":page" element={<Messages />} />
            <Route path="message/:id" element={<MessageComponent />} />
          </Route>
        </Route>
      )}

      {isFeatureEnabled("Inbox") && (
        <Route path="preview/:externalMessageId/:externalAttachmentId" element={<AttachmentPreviewRedirect />} />
      )}

      <Route path="*" element={<Navigate replace to={getHomeRoute()} />} />
    </Routes>
  );
};

export default Pages;
